// useLoginForm.ts
'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { loginSchema  } from '@/auth.config';
import { loginAndBridge } from '@/server/actions/login';
import { LoginFormValues } from '@/types/auth';

type LoginState = 'idle' | 'submitting' | 'redirecting';

export const useLoginForm = () => {
  const [state, setState] = useState<LoginState>('idle');
  const router = useRouter();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  });

  const handleSubmit = async (values: LoginFormValues) => {
    if (state !== 'idle') return;

    setState('submitting');
    try {
      const res = await loginAndBridge(values);

      if (!res.ok) {
        setState('idle');
        form.setError('password', {
          type: 'manual',
          message: res.message || 'Login failed',
        });
        return;
      }

      setState('redirecting');
      router.push('/cameras');
    } catch (err) {
      const error = err as Error;
      setState('idle');
      form.setError('password', {
        type: 'manual',
        message: error?.message || 'Unexpected error',
      });
    }
  };

  const isSubmitting = state === 'submitting';
  const isRedirecting = state === 'redirecting';
  const isBusy = isSubmitting || isRedirecting;

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting,
    isRedirecting,
    isBusy,
  };
};
